import { useEffect, useRef, useState } from "react";
import { MapPin, Loader2, CheckCircle2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";

const GEO_URL = "https://data.geopf.fr/geocodage/search";

async function searchAddresses(q, departments) {
  const params = new URLSearchParams({ q, limit: "8", autocomplete: "1", index: "address", depcode: departments.join(",") });
  const res = await fetch(`${GEO_URL}?${params}`);
  if (!res.ok) return { results: [], needsNumber: false };
  const data = await res.json();
  const feats = (data.features || []).map((f) => f.properties).filter((p) => p.postcode && p.type !== "municipality");
  const full = feats.filter((p) => p.type !== "street").map((p) => ({ label: p.label, postcode: p.postcode, city: p.city }));
  return { results: full, needsNumber: full.length === 0 && feats.length > 0 };
}

export const AddressAutocomplete = ({ departments, selected, onSelect }) => {
  const [text, setText] = useState(selected?.label || "");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [needsNumber, setNeedsNumber] = useState(false);
  const timer = useRef();

  useEffect(() => {
    clearTimeout(timer.current);
    if (selected || text.trim().length < 3 || !departments.length) { setResults([]); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try { const r = await searchAddresses(text.trim(), departments); setResults(r.results); setNeedsNumber(r.needsNumber); setOpen(true); }
      catch (e) { setResults([]); }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(timer.current);
  }, [text, departments, selected]);

  const change = (v) => { setText(v); if (selected) onSelect(null); };
  const pick = (r) => { setText(r.label); setOpen(false); setResults([]); onSelect(r); };

  return (
    <div className="relative">
      <Textarea data-testid="checkout-address" value={text} rows={2} onChange={(e) => change(e.target.value)}
        placeholder="Commencez à taper : 12 rue de la Gare, Niort…"
        className={`bg-[#161926] mt-1 text-slate-100 pr-9 ${selected ? "border-emerald-500/60" : "border-amber-500/20"}`} />
      <span className="absolute right-3 top-4">
        {loading ? <Loader2 className="w-4 h-4 text-amber-400 animate-spin" /> : selected ? <CheckCircle2 data-testid="address-selected-icon" className="w-4 h-4 text-emerald-400" /> : null}
      </span>
      {open && !selected && text.trim().length >= 3 && !loading && (
        <div data-testid="address-suggestions" className="mt-1 rounded-xl border border-amber-500/25 bg-[#0F1320] overflow-hidden max-h-60 overflow-y-auto">
          {results.length === 0 ? (
            <p data-testid="address-no-result" className="text-xs text-slate-400 px-3 py-3">{needsNumber ? "Ajoutez votre numéro de rue pour voir votre adresse exacte." : "Aucune adresse trouvée dans nos zones de livraison."}</p>
          ) : results.map((r, i) => (
            <button key={r.label + i} type="button" data-testid={`address-suggestion-${i}`} onClick={() => pick(r)}
              className="w-full text-left px-3 py-2.5 text-sm text-slate-200 hover:bg-amber-500/10 flex items-start gap-2 border-b border-amber-500/10 last:border-0">
              <MapPin className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" /> {r.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
